import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sparkle, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-slate-900 text-white flex-col justify-between p-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <Sparkle size={20} weight="fill" />
          </div>
          <div className="font-heading font-bold text-xl">Kollab</div>
        </div>
        <div className="max-w-md">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">Account recovery</div>
          <h1 className="font-heading text-5xl font-bold leading-tight mb-4">
            Locked out? Happens to the best of us.
          </h1>
          <p className="text-slate-400 text-base leading-relaxed">
            Enter your email, we'll send you a fresh reset link that expires in an hour. No drama.
          </p>
        </div>
        <div className="text-xs text-slate-500">Kollab · Password reset</div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6" data-testid="back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>

          {sent ? (
            <div>
              <div className="w-12 h-12 rounded-md bg-emerald-50 flex items-center justify-center mb-4">
                <EnvelopeSimple size={22} className="text-emerald-600" weight="duotone" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-slate-900">Check your inbox</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                If an account exists for <span className="font-medium text-slate-900">{email}</span>, we've sent a reset link.
                It expires in 1 hour. Also check spam.
              </p>
              <Link
                to="/login"
                className="inline-block mt-6 text-sm font-medium text-blue-600 hover:text-blue-700"
                data-testid="back-link"
              >
                Back to sign in →
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Password recovery</div>
                <h2 className="font-heading text-3xl font-bold text-slate-900">Reset your password</h2>
                <p className="text-sm text-slate-500 mt-2">We'll email you a link to pick a new one.</p>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@agency.com"
                    required
                    className="mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
